import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area, Cell } from "recharts";
import { PageBody, PageHeader } from "@/components/AppLayout";
import { boardMetrics } from "@/lib/api";
import StewardshipLedger from "@/components/StewardshipLedger";
import { DASHBOARD } from "@/constants/testIds";

function money(n) { return `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`; }

export default function Board() {
  const [m, setM] = useState(null);

  useEffect(() => { boardMetrics().then(setM); }, []);

  if (!m) {
    return (
      <>
        <PageHeader title="Board dashboard" subtitle="The numbers your board actually needs." />
        <PageBody>
          <div className="text-sm text-muted-foreground">Loading…</div>
        </PageBody>
      </>
    );
  }

  const programData = m.spend_by_program.map((p) => ({
    name: p.program_name,
    budget: p.budget,
    spent: p.spent,
  }));

  const trendData = m.monthly_trend;

  return (
    <>
      <PageHeader title="Board dashboard" subtitle="The numbers your board actually needs." />
      <PageBody>
        <div className="mb-8">
          <StewardshipLedger />
        </div>
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Metric label="Total budget" value={money(m.total_budget)} />
          <Metric label="Total spent" value={money(m.total_spent)} sub={`${((m.total_spent / Math.max(m.total_budget, 1)) * 100).toFixed(0)}% utilization`} />
          <Metric label="Pending" value={money(m.pending_amount)} sub={`${m.pending_count} items`} />
          <Metric label="To reimburse" value={money(m.reimbursable_amount)} sub={`${m.approved_count} approved`} />
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          <div data-testid={DASHBOARD.programChart} className="border border-border bg-card rounded-md p-6">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="serif text-lg text-primary">Budget vs spent by program</div>
                <div className="text-xs text-muted-foreground mt-0.5">Live, not last quarter.</div>
              </div>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <BarChart data={programData} margin={{ top: 8, right: 8, bottom: 24, left: 8 }}>
                  <CartesianGrid stroke="hsl(220 20% 90%)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: "hsl(220 20% 40%)" }} tickLine={false} axisLine={{ stroke: "hsl(220 20% 80%)" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(220 20% 40%)" }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid hsl(220 20% 90%)" }} />
                  <Bar dataKey="budget" fill="hsl(220 20% 80%)" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="spent" radius={[2, 2, 0, 0]}>
                    {programData.map((p, idx) => {
                      const pct = p.budget > 0 ? p.spent / p.budget : 0;
                      let color = "hsl(225 45% 20%)";
                      if (pct > 1) color = "hsl(0 70% 45%)";
                      else if (pct >= 0.8) color = "hsl(45 85% 50%)";
                      return <Cell key={idx} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div data-testid={DASHBOARD.trendChart} className="border border-border bg-card rounded-md p-6">
            <div className="flex items-baseline justify-between mb-4">
              <div>
                <div className="serif text-lg text-primary">Spending trend</div>
                <div className="text-xs text-muted-foreground mt-0.5">Approved + reimbursed, last 6 months.</div>
              </div>
            </div>
            <div style={{ width: "100%", height: 280 }}>
              <ResponsiveContainer>
                <AreaChart data={trendData} margin={{ top: 8, right: 8, bottom: 24, left: 8 }}>
                  <defs>
                    <linearGradient id="trendGrad" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="hsl(45 85% 55%)" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="hsl(45 85% 55%)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(220 20% 90%)" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(220 20% 40%)" }} tickLine={false} axisLine={{ stroke: "hsl(220 20% 80%)" }} />
                  <YAxis tick={{ fontSize: 11, fill: "hsl(220 20% 40%)" }} tickLine={false} axisLine={false} tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid hsl(220 20% 90%)" }} />
                  <Area type="monotone" dataKey="amount" stroke="hsl(45 85% 45%)" strokeWidth={2} fill="url(#trendGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </PageBody>
    </>
  );
}

function Metric({ label, value, sub }) {
  return (
    <div className="border border-border bg-card rounded-md p-6">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="serif text-2xl text-primary mt-2 tabular mono">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}
